import { Currency, ParameterType, PostHogExperiment, SortingType } from '@/study-search/enums';
import { FilterFacets, ParameterValue, SearchStudyCardInfo } from '@/study-search/domain';

import { IQuery, IUrlQueryConstructor } from '../interfaces/url.interfaces';
import { IGetters } from '../interfaces';
import { Filter } from '../models/filter/Filter';
import { UrlQueryConstructor } from '../models/url/UrlQueryConstructor';
import state from './state';
import { CurrencyValue } from '../models/currency/CurrencyValue';
import { PortalType } from '@studyportals/domain-client';
import { FilterOptionInformation } from '@studyportals/search-filters';
import { PortalDefinition } from '../models/definition';
import { countryGetByIsoCode } from '@studyportals/static-domain-data/countries/country-get-by-iso-code'
import { IContent } from '../interfaces/content.interface';

const urlQueryConstructor: IUrlQueryConstructor = new UrlQueryConstructor();

const getters: IGetters = {
	getApplicationStatus: (): string => state.applicationStatus,

	getAreDependenciesLoaded: (): boolean => state.areDependenciesLoaded,

	getCanonical: (): string => state.canonical,

	getCurrency: (): string => {
		return state.userCurrency || Currency.EURO;
	},

	isEea: (): boolean => state.eea,

	getCurrencyRates: (): CurrencyValue[] => {
		return state.currencyRates;
	},

	getFacets: (shortName: ParameterType): FilterFacets => {
		return state.facets.filter((_) => _.type === shortName)[0];
	},

	getAllFacets: (): FilterFacets[] => state.facets,

	isStructuredFilter: (type: ParameterType): boolean => {
		return state.filters.filter((_) => _.type === type)[0].structured;
	},

	getFilters: (): Filter[] => state.filters,

	getFilterCollapseOption: (filterType: ParameterType): boolean => {
		const filter = state.filters.find((_) => _.type === filterType);
		return filter === undefined || filter.collapsed;
	},

	getFilterOptionInformation: (filterType: string): FilterOptionInformation[] => {
		const filterDefinition = state.portalDefinition.getFilterDefinition(filterType);
		if (filterDefinition !== undefined) {
			return filterDefinition.filterInformation;
		}

		return [];
	},

	getFilterOptionInformationById: (filterType: string, id: string): FilterOptionInformation | null => {
		const filterDefinition = state.portalDefinition.getFilterDefinition(filterType);
		if (!filterDefinition) {
			return null;
		}

		return filterDefinition.getFilterOption(id);
	},

	getFilterValues: (shortName: ParameterType): string[] => {
		const filters = state.filters.filter((_) => _.type === shortName);
		if (filters.length === 0) {
			return [];
		}
		const filter = filters[0];
		return filter.values;
	},

	isGAIQuestionnaireTaken: (): boolean => {
		return state.isGAIQuestionnaireTaken;
	},

	getMetaDescriptionPortalType: (): string => {
		return state.portalDefinition.metaDescriptionPortalType;
	},

	getMetaRefreshInfo: (): string => {
		return state.metaRefreshInfo;
	},

	isNotTracked: (id: string): boolean => {
		return !state.trackedStudyCardIds.includes(id);
	},

	getPageNumber: (): number => {
		return state.page.pageNumber;
	},

	getPortalPath: (): string => {
		return state.portalDefinition.path;
	},

	getFaqStaticContent: (): IContent | null => {
		return state.faqStaticContent;
	},

	getPortalDefinition: (): PortalDefinition => {
		return state.portalDefinition;
	},

	getPortalType: (): PortalType => state.portalType,

	getPreviousPageNumber: (): number => {
		return state.page.previousPageNumber;
	},

	getPreviousSearchQuery: (): ParameterValue[] => {
		return state.previousSearchQuery;
	},

	getQuery: (): IQuery => {
		return urlQueryConstructor.getQuery(state.filters, getters.getSorting());
	},

	getSearchResults: (): SearchStudyCardInfo[] => state.searchResults,

	getSearchResultsCount: (): number => state.searchResultsCount,

	getTitle: (parameterType: ParameterType): string => {
		const filter = state.filters.filter((_) => _.type === parameterType)[0];

		if (filter.values.length !== 1) {
			return '';
		}

		const option: string = filter.values[0];
		const filterDefinition = filter.definition;

		if (filterDefinition !== undefined && filterDefinition.indexableFilterOptionValues.includes(option)) {
			return filterDefinition.filterInformation.filter((_) => _.optionValue === option)[0].optionTitle;
		}

		return '';
	},

	getTopCountryIds: (): string[] => {
		return state.portalDefinition.topCountries;
	},

	getTotalSearchResults: (): number => {
		const filterFacets = state.facets.find((_) => _.type === ParameterType.EDUCATION_LEVEL);
		if (!filterFacets) {
			return 0;
		}

		if (state.portalType === PortalType.DISTANCE_LEARNING) {
			const selectedLevels = getters.getFilterValues(ParameterType.EDUCATION_LEVEL);
			if (selectedLevels.length === 0) {
				const values = filterFacets.values.map((_) => _.count);
				if (values.length === 0) {
					return 0;
				}

				return values.reduce((current, next) => current + next);
			}

			const levelFacetValues = filterFacets.values.filter((_) => selectedLevels.includes(_.identity));
			if (levelFacetValues.length === 0) {
				return 0;
			}

			return levelFacetValues.map((_) => _.count).reduce((current, next) => current + next);
		}

		const facetValue = filterFacets.values.find((_) => _.identity as PortalType === state.portalType);
		if (facetValue !== undefined) {
			return facetValue.count;
		}

		return 0;
	},

	getUserCountryIso: (): string | undefined => {
		return state.userCountryIso;
	},

	getUserCountryId: (): string | undefined => {
		if (state.userCountryIso === undefined) return;
		return countryGetByIsoCode(state.userCountryIso)?.id;
	},


	getCountryCheckboxVisibility: (): string[] => {
		return state.expandedCountryCheckboxes;
	},

	getUserCountryOfResidence: (): number => {
		return state.countryResidence;
	},

	getSorting: (): SortingType => {
		return state.sorting;
	},

	getPreviousSorting: (): SortingType => {
		return state.previousSorting;
	},
	getCurrentPostHogExperiment: (): PostHogExperiment | undefined => {
		return state.currentPostHogExperiment;
	}
};

export default getters;
